import { Image, Text, View } from "@react-pdf/renderer";

import { format, parse } from "date-fns";

import {
  CLINIC_LOGO_MAX_HEIGHT,
  CLINIC_LOGO_MAX_WIDTH
} from "components/print-header/print-header-report/clinic-logo-constants";
import { Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import { Physician } from "services/types/physician.type";
import { MinimalStaffMember } from "services/types/staff.type";
import {
  formatPhoneNumber,
  formatResourceUrl,
  getAgeInMonthsOrYears,
  getFullName
} from "utils/common";
import { getPhysiciansAsText } from "utils/physicians.utils";

import PdfDocumentLabelValue from "../components/pdf-document-label-value.component";

type PdfDocumentHeaderReportProps = {
  clinic?: Clinic;
  staffMember?: MinimalStaffMember;
  patient?: Patient;
  dateOfVisit?: string | number | Date;
  showDateOfVisit?: boolean;
  physicians?: Array<Physician | undefined>;
};
const DATE_MASK = "MM/dd/yyyy";
export default function PdfDocumentHeaderReport({
  clinic,
  staffMember,
  patient,
  dateOfVisit,
  showDateOfVisit = true,
  physicians
}: PdfDocumentHeaderReportProps) {
  const staffMemberName = staffMember
    ? getFullName({
        firstName: staffMember.first_name || "",
        lastName: staffMember.last_name || "",
        title: staffMember.title,
        suffix: staffMember.suffix
      })
    : "";

  const address_line1 = [clinic?.street_address_1, clinic?.street_address_2]
    .filter(Boolean)
    .join(", ");

  const address_line2 = [clinic?.city, clinic?.state, clinic?.zip_code]
    .filter(Boolean)
    .join(", ");

  const clinicPhone =
    formatPhoneNumber(clinic?.phone || "", "parenthesis") || "";

  const patientPhone =
    formatPhoneNumber(
      patient?.phone || patient?.secondary_phone || "",
      "parenthesis"
    ) || "";

  const patientName = patient
    ? getFullName({
        firstName: patient.first_name ?? "",
        lastName: patient.last_name ?? "",
        suffix: patient.suffix
      })
    : "";

  const patientSex = patient?.sex ?? "";

  const patientDateOfBirth = patient?.birthdate
    ? format(
        typeof patient?.birthdate === "string"
          ? parse(patient?.birthdate, "yyyy-MM-dd", new Date())
          : new Date(patient?.birthdate),
        DATE_MASK
      )
    : "";

  const patientDateOfVisit =
    dateOfVisit && showDateOfVisit
      ? format(new Date(dateOfVisit), DATE_MASK)
      : "";

  const physiciansText = getPhysiciansAsText(physicians);
  const logo = clinic?.logo || clinic?.practice?.logo;

  return (
    <View wrap={false} style={{ width: "100%" }}>
      <View
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "8px"
        }}
      >
        <View
          style={{
            width: "100%",
            height: "3px",
            backgroundColor: "#381337",
            marginBottom: "4px"
          }}
        ></View>
        <View
          style={{
            width: "30%"
          }}
        >
          <View
            style={{
              height: parseFloat(CLINIC_LOGO_MAX_HEIGHT) * 0.75,
              width: parseFloat(CLINIC_LOGO_MAX_WIDTH) * 0.75
            }}
          >
            {Boolean(logo) && (
              <Image
                style={{
                  objectFit: "scale-down",
                  marginBottom: "0px"
                }}
                src={formatResourceUrl(logo, true)}
              />
            )}
          </View>
        </View>

        <View
          style={{
            width: "360px"
          }}
        >
          <PdfDocumentLabelValue
            labelWidth="75px"
            label="Practice name:"
            value={clinic?.display_name ?? clinic?.practice?.name ?? ""}
            style={{ paddingTop: "0px" }}
          />
          <PdfDocumentLabelValue
            labelWidth="75px"
            label="Provider:"
            value={staffMemberName}
          />
          <PdfDocumentLabelValue
            labelWidth="75px"
            label="Address:"
            value={address_line1}
          />
          <PdfDocumentLabelValue
            labelWidth="75px"
            label="&nbsp;"
            value={address_line2}
          />
          <PdfDocumentLabelValue
            labelWidth="75px"
            label="Phone:"
            value={clinicPhone}
            noBorder
          />
        </View>

        <View
          style={{
            width: "294px"
          }}
        >
          <PdfDocumentLabelValue
            label="Name:"
            value={patientName}
            style={{ paddingTop: "0px" }}
          />

          <PdfDocumentLabelValue
            label="Sex:"
            value={
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "14px"
                }}
              >
                <Text style={{ fontSize: "14px", color: "#000000" }}>
                  {patientSex}
                </Text>
                <View style={{ paddingTop: "2px" }}>
                  <Text style={{ fontSize: "11px", color: "#000000" }}>
                    Age:
                  </Text>
                </View>
                <Text style={{ fontSize: "14px", color: "#000000" }}>
                  {getAgeInMonthsOrYears(
                    patient?.age_years ?? "",
                    patient?.birthdate
                  )}
                </Text>

                <View style={{ paddingTop: "2px" }}>
                  <Text style={{ fontSize: "11px", color: "#000000" }}>
                    DOB:
                  </Text>
                </View>
                <Text style={{ fontSize: "14px", color: "#000000" }}>
                  {patientDateOfBirth}
                </Text>
              </View>
            }
          />

          {showDateOfVisit && (
            <PdfDocumentLabelValue
              label="Date of Visit:"
              value={patientDateOfVisit}
            />
          )}
          <PdfDocumentLabelValue label="Phone:" value={patientPhone} />
          {physiciansText && (
            <PdfDocumentLabelValue
              label="Physicians"
              value={physiciansText}
              noBorder
            />
          )}
        </View>
        <View
          style={{
            width: "100%",
            height: "3px",
            backgroundColor: "#381337",
            marginTop: "4px",
            marginBottom: "5px"
          }}
        ></View>
      </View>
    </View>
  );
}
